import * as React from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';

import { CopyOutlined as FileCopyOutlined, LinkOutlined } from '@ant-design/icons';
import { Button, Table } from 'antd';

import { CHECKOUT_ELEMENTS_URL } from 'settings';
import { BaseOfferTag } from 'v2/components/Shared/BaseOfferTag';
import { Space } from 'v2/components/Shared/Space';
import Truncate from 'v2/components/Shared/TruncateText';
import { useProductOffers } from 'v2/hooks/useProductOffers';
import type { ProductOffer } from 'v2/interfaces/product';
import eduzzAccountsService from 'v2/services/eduzzAccount';

type ListProps = {
  isAffiliate: boolean;
  productId: number;
  handleLinksClick: (offer: ProductOffer) => void;
};

function Component({ isAffiliate, productId, handleLinksClick }: ListProps) {
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const offersQuery = useProductOffers(
    {
      productId
    },
    { enabled: !!productId }
  );

  function getBaseOffer(offer: ProductOffer) {
    return offer.prices?.find(price => price.currency.code === 'BRL');
  }

  function formatPriceBasedOnBaseOffer(offer: ProductOffer) {
    const baseOffer = getBaseOffer(offer);
    if (!baseOffer) return 'R$ 0,00';
    return baseOffer.value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  function mountOfferLink(offer: ProductOffer) {
    const url = new URL(`${CHECKOUT_ELEMENTS_URL}/${offer.key}`);

    if (isAffiliate && user) {
      url.searchParams.append('a', String(user.id));
    }

    return String(url);
  }

  function copyLinkToClipboard(link: string) {
    navigator.clipboard.writeText(link);
    toast.success('Link copiado para a área de transferência');
  }

  const columns = [
    {
      title: 'Nome da oferta',
      key: 'name',
      render: (_: any, offer: ProductOffer) => (
        <span className='whitespace-nowrap'>
          <Truncate count={30} style={{ display: 'inline-block' }}>
            {offer.name}
          </Truncate>
          {offer.is_default && (
            <>
              <Space axis='horizontal' size={8} style={{ display: 'inline-block' }} />
              <BaseOfferTag />
            </>
          )}
        </span>
      )
    },
    {
      title: 'Valor base',
      key: 'value',
      width: 112,
      render: (_: any, offer: ProductOffer) => formatPriceBasedOnBaseOffer(offer)
    },
    {
      title: 'Preços',
      key: 'prices',
      width: 60,
      align: 'center' as const,
      render: (_: any, offer: ProductOffer) => String(offer.prices?.length || 0).padStart(2, '0')
    },
    {
      title: 'Página de pagamento (Checkout Sun)',
      key: 'link',
      render: (_: any, offer: ProductOffer, index: number) => (
        <div className='flex items-center gap-2'>
          <LinkOutlined style={{ fontSize: 18 }} />
          <a
            href={mountOfferLink(offer)}
            target='_blank'
            rel='noreferrer'
            className='min-w-[230px] text-sm text-black hover:underline'
          >
            {mountOfferLink(offer)}
          </a>
          <Button
            type='text'
            size='small'
            id={`offer-copy-link-${index}`}
            onClick={() => copyLinkToClipboard(mountOfferLink(offer))}
            icon={<FileCopyOutlined style={{ fontSize: 18 }} />}
          />
        </div>
      )
    },
    {
      title: '',
      key: 'actions',
      width: 180,
      align: 'center' as const,
      render: (_: any, offer: ProductOffer) => (
        <Button type='text' onClick={() => handleLinksClick(offer)}>
          Links desta oferta
        </Button>
      )
    }
  ];

  return (
    <Table
      columns={columns}
      dataSource={offersQuery.data ?? []}
      rowKey='id'
      pagination={false}
      size='small'
      loading={offersQuery.isLoading}
      locale={{
        emptyText: offersQuery.isError
          ? 'Ocorreu um erro ao carregar as ofertas'
          : 'Nenhuma oferta cadastrada para este produto'
      }}
    />
  );
}

const List = React.memo(Component);

export { List };
